import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-hot-toast";
import { PhotoIcon } from "@heroicons/react/24/outline";
import { PMDialog } from "../../utils/PMDialog";
import { DocumentUploadSectionV3 } from "../Media/DocumentUploadSectionV3";
import { completeProjectEventResource } from "../../../api/projectPlan";
import { createNote } from "../../../api/notes";
import { getDocumentCount } from "../../../api/document";
import axios from "../../../api/axios";

const ACTIVITY_STATUSES = ["open", "in_progress", "completed", "on_hold"];

const WORK_ORDER_OUTCOMES = ["ongoing", "finished"];

const stripHtml = (value) =>
  String(value || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();

export function PlanEventCompleteDialog({
  open,
  onClose,
  linkedWorkOrders = [],
  linkedActivities = [],
  projectId,
  resourceId,
  onCompleted,
}) {
  const { t } = useTranslation();
  const [submitting, setSubmitting] = useState(false);
  const [workDescription, setWorkDescription] = useState("");
  const [activityStatuses, setActivityStatuses] = useState({});
  const [workOrderOutcomes, setWorkOrderOutcomes] = useState({});
  const [photoCounts, setPhotoCounts] = useState({});

  const requiresDetailedComplete = linkedWorkOrders.length > 0 || linkedActivities.length > 0;

  useEffect(() => {
    if (!open) return;
    setWorkDescription("");
    setActivityStatuses(
      Object.fromEntries(
        linkedActivities.map((pa) => [String(pa.id), pa.status || "in_progress"])
      )
    );
    setWorkOrderOutcomes(
      Object.fromEntries(
        linkedWorkOrders.map((wo) => [String(wo.id), wo.is_completed ? "finished" : "ongoing"])
      )
    );
  }, [open, linkedActivities, linkedWorkOrders]);

  const loadPhotoCounts = useCallback(async () => {
    if (!linkedWorkOrders.length) {
      setPhotoCounts({});
      return;
    }
    const entries = await Promise.all(
      linkedWorkOrders.map(async (wo) => {
        try {
          const res = await getDocumentCount("work_order_description", String(wo.id));
          const count = res?.count ?? res?.data?.count ?? 0;
          return [String(wo.id), Number(count) || 0];
        } catch {
          return [String(wo.id), 0];
        }
      })
    );
    setPhotoCounts(Object.fromEntries(entries));
  }, [linkedWorkOrders]);

  useEffect(() => {
    if (open) loadPhotoCounts();
  }, [open, loadPhotoCounts]);

  const validationError = useMemo(() => {
    if (!requiresDetailedComplete) return null;

    if (linkedActivities.some((pa) => !activityStatuses[String(pa.id)])) {
      return t("calendar.plan_event.complete_validation.activity_status");
    }

    if (
      linkedWorkOrders.length > 0 &&
      stripHtml(workDescription).length < 5
    ) {
      return t("calendar.plan_event.complete_validation.description");
    }

    if (linkedWorkOrders.some((wo) => (photoCounts[String(wo.id)] || 0) === 0)) {
      return t("calendar.plan_event.complete_validation.photos");
    }

    return null;
  }, [
    requiresDetailedComplete,
    linkedActivities,
    activityStatuses,
    workDescription,
    linkedWorkOrders,
    photoCounts,
    t,
  ]);

  const handleSubmit = async () => {
    if (!resourceId) return;
    if (validationError) {
      toast.error(validationError);
      return;
    }

    setSubmitting(true);
    try {
      if (requiresDetailedComplete) {
        const descriptionHtml = workDescription.trim().startsWith("<")
          ? workDescription.trim()
          : `<p>${workDescription.trim().replace(/\n/g, "<br/>")}</p>`;

        await Promise.all(
          linkedActivities.map((pa) =>
            axios.patch(`/projects/${projectId}/project_activities/${pa.id}`, {
              project_activity: { status: activityStatuses[String(pa.id)] },
            })
          )
        );

        await Promise.all(
          linkedWorkOrders.map(async (wo) => {
            if (stripHtml(workDescription).length >= 5) {
              await createNote({
                note: descriptionHtml,
                module: "work_order",
                module_param: String(wo.id),
                documents: [],
                tags: [],
              });
            }

            const outcome = workOrderOutcomes[String(wo.id)] || "ongoing";
            if (outcome === "finished" && !wo.is_completed) {
              await axios.patch(`/work_orders/${wo.id}`, {
                work_order: { is_completed: true, status: "finished" },
              });
            }
          })
        );
      }

      await completeProjectEventResource(resourceId);
      toast.success(t("calendar.plan_event.completed"));
      onCompleted?.();
      onClose?.();
    } catch (err) {
      toast.error(
        err?.response?.data?.error ||
          err?.response?.data?.errors?.join?.(", ") ||
          t("calendar.plan_event.complete_error")
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (!requiresDetailedComplete) {
    return (
      <PMDialog
        open={open}
        onClose={onClose}
        title={t("calendar.plan_event.complete_dialog.title")}
        size="md"
      >
        <p className="text-sm text-gray-600">{t("calendar.plan_event.complete_dialog.simple_confirm")}</p>
        <div className="mt-6 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            {t("Cancel")}
          </button>
          <button
            type="button"
            disabled={submitting}
            onClick={handleSubmit}
            className="rounded-md bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
          >
            {t("calendar.plan_event.mark_my_task_complete")}
          </button>
        </div>
      </PMDialog>
    );
  }

  return (
    <PMDialog
      open={open}
      onClose={onClose}
      title={t("calendar.plan_event.complete_dialog.title")}
      size="3xl"
    >
      <div className="space-y-6">
        <p className="text-sm text-gray-600">{t("calendar.plan_event.complete_dialog.intro")}</p>

        {linkedActivities.length > 0 ? (
          <div className="space-y-3 rounded-lg border border-gray-200 p-4">
            <h3 className="text-sm font-semibold text-gray-900">
              {t("calendar.plan_event.complete_dialog.activities")}
            </h3>
            {linkedActivities.map((pa) => (
              <div
                key={pa.id}
                className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between"
              >
                <div className="min-w-0">
                  <div className="text-sm font-medium text-gray-900">
                    {pa.display_title || pa.title || `#${pa.id}`}
                  </div>
                  {pa.is_primary ? (
                    <span className="text-xs text-teal-700">{t("project.plan.primary_link")}</span>
                  ) : null}
                </div>
                <select
                  value={activityStatuses[String(pa.id)] || "in_progress"}
                  onChange={(e) =>
                    setActivityStatuses((prev) => ({
                      ...prev,
                      [String(pa.id)]: e.target.value,
                    }))
                  }
                  className="rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500"
                >
                  {ACTIVITY_STATUSES.map((status) => (
                    <option key={status} value={status}>
                      {t(`project.activities.status.${status}`)}
                    </option>
                  ))}
                </select>
              </div>
            ))}
          </div>
        ) : null}

        {linkedWorkOrders.length > 0 ? (
          <div className="space-y-3 rounded-lg border border-gray-200 p-4">
            <h3 className="text-sm font-semibold text-gray-900">
              {t("calendar.plan_event.complete_dialog.work_orders")}
            </h3>
            {linkedWorkOrders.map((wo) => (
              <div
                key={wo.id}
                className="space-y-3 border-t border-gray-100 pt-3 first:border-t-0 first:pt-0"
              >
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-gray-900">
                      {wo.displayTitle || wo.title || `#${wo.id}`}
                    </div>
                    {wo.is_primary ? (
                      <span className="text-xs text-teal-700">{t("project.plan.primary_link")}</span>
                    ) : null}
                  </div>
                  <select
                    value={workOrderOutcomes[String(wo.id)] || "ongoing"}
                    onChange={(e) =>
                      setWorkOrderOutcomes((prev) => ({
                        ...prev,
                        [String(wo.id)]: e.target.value,
                      }))
                    }
                    className="rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500"
                  >
                    {WORK_ORDER_OUTCOMES.map((value) => (
                      <option key={value} value={value}>
                        {t(`calendar.plan_event.complete_dialog.work_order_outcome.${value}`)}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="rounded-md bg-gray-50 p-3">
                  <div className="mb-2 flex items-center gap-2 text-sm font-medium text-gray-900">
                    <PhotoIcon className="h-4 w-4" />
                    {t("calendar.plan_event.photos")}
                    <span className="text-xs font-normal text-gray-500">
                      ({photoCounts[String(wo.id)] || 0}{" "}
                      {t("calendar.plan_event.complete_dialog.photos_count")})
                    </span>
                  </div>
                  <DocumentUploadSectionV3
                    module="work_order_description"
                    moduleParam={wo.id}
                    parentCallback={() => loadPhotoCounts()}
                  />
                </div>
              </div>
            ))}
          </div>
        ) : null}

        {linkedWorkOrders.length > 0 ? (
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              {t("calendar.plan_event.complete_dialog.work_description")}
            </label>
            <textarea
              value={workDescription}
              onChange={(e) => setWorkDescription(e.target.value)}
              rows={5}
              placeholder={t("calendar.plan_event.complete_dialog.work_description_placeholder")}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500"
            />
            <p className="text-xs text-gray-500">
              {t("calendar.plan_event.complete_dialog.work_description_hint")}
            </p>
          </div>
        ) : null}

        {validationError ? (
          <p className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800">{validationError}</p>
        ) : null}

        <div className="flex flex-col-reverse gap-2 border-t pt-4 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            {t("Cancel")}
          </button>
          <button
            type="button"
            disabled={submitting}
            onClick={handleSubmit}
            className="rounded-md bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
          >
            {t("calendar.plan_event.complete_dialog.submit")}
          </button>
        </div>
      </div>
    </PMDialog>
  );
}
